/** 
 * @module Verification (Plan Verification)
 * @description Component for managing verifier, uploading verification documents, stamping and accept, reject or retain plan verification.
 */
import React, { useEffect, useState, useContext, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment/moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { MDBCol, MDBRow } from "mdbreact";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Download } from "@mui/icons-material";
import PlanUploadModal from "../Documents/PlanUploadModal";
import "./Verification.scss";
import { Button, COLORS, GenericDropdown, Input, Label, Modal, Switch, RadioInput } from "abs-ui-components";
import AbsModal from "../../../common/absModal/AbsModal";
import CustomTable from "../../../common/customTable/CustomTablePlanVerification";
import VerificationSelectFiles from "./VerificationSelectFiles";
import { accessAPIForResponse } from "../../../services/api-services/api-implementations";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import AutocompleteDropdown from "../../../common/autocompleteDropdown/AutocompleteDropdown";
import { ClaimsContext } from "../../../services/ClaimsService";
import { ClaimType } from "../../../constants/claims";
import { DATE_FORMAT_DD_MMM_YYYY } from "../../../constants/dateFormats";
import ContainedLoader from "../../../common/Loader/ContainedLoader";
import { FeatureType } from "../../../constants/FeatureToggles";
import { SEEMPTYPE, AMENDMENTTYPE } from "../WaypointEnums";
/**
 * @param {object} props
 * @param {object} props.planDetails
 * @param {boolean} [props.isActive]
 * @param {object} props.planVerificationFlags
 * @param {Function} props.refreshState
 * @param {Array} [props.associatedDocumentsData]
 */
const Verification = ({
  planDetails,
  isActive = true,
  planVerificationFlags,
  refreshState,
  associatedDocumentsData: incomingDocuments = [],
  selectedWaypointAmendType,
  setSelectedWaypointAmendType,
  selectedWaypointType,
  setSelectedWaypointType

}) => {
  /* ------------------------------ State ------------------------------ */
  const { userHasClaim, userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);

  const [selectedVerifier, setSelectedVerifier] = useState();
  const [selectedVerifyDate, setSelectedVerifyDate] = useState();

  const [isSelectFilesModalOpen, setIsSelectFilesModalOpen] = useState(false);
  const [isUploadPlanModalOpen, setIsUploadPlanModalOpen] = useState(false);
  const [isApprovedPlanUploadMode, setIsApprovedPlanUploadMode] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [associatedDocumentsData, setAssociatedDocumentsData] = useState([]);

  const [loader, setLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [verificationEnabled, SetverificationEnabled] = useState(false);
  const [showUpdateVerifierModal, setShowUpdateVerifierModal] = useState(false);

  const [templateType, setTemplateType] = useState("");
  const [stampingOnSeemp, setStampingOnSeemp] = useState("No stamp");

  const [rejectedActions, setRejectedActions] = useState([]);
  const [retainedActions, setRetainedActions] = useState([]);

    const amendmentOptions = [
		{ label: "Minor", value: "Minor" },
		{ label: "Major", value: "Major" },
	  ];
    const typeOptions = [
      { label: "New", value: "New" },
      { label: "Client Amendment", value: "ClientAmendment" },
    ];
    const [waypointSubmissionStatus, setWaypointSubmissionStatus] = useState("");
    const [iswayPointMessageModalOpen, setIsWayPointMessageModalOpen] = useState(false);
    const [wayPointErrorMessage, setwayPointErrorMessage] = useState("");
    const [waypointStatusMessage, setWaypointStatusMessage] = useState("");
    const [waypointSubmitDateTime, setWaypointSubmitDateTime] = useState("");

    const planType = planDetails?.Plan ;
    const isWaypointSubmissionEnabled = planDetails?.Flag === "Liberia" && (planType === "SEEMP II" || planType === "SEEMP III");
   

  /* --------------------------- Derived flags ------------------------- */
  const showAssignVerifierButton = planVerificationFlags?.data?.submitToVerifierButton || false;

  /* ------------------------------ Forms ------------------------------ */
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  /* ------------------------------ Effects ---------------------------- */

  // Normalize rejected/retained actions from payload for display
  useEffect(() => {
    if (planDetails?.VerificationStatus === "Under Review" || planDetails?.VerificationStatus === "Retained") {
      const normalize = (input) => {
        const arr = Array.isArray(input) ? input : input && typeof input === "object" ? [input] : [];
        return arr
          .map((a) => ({
            actionBy: a?.ActionBy ?? a?.actionBy ?? "",
            reason: a?.Reason ?? a?.reason ?? "",
          }))
          .filter((x) => x.actionBy || x.reason);
      };

      setRejectedActions(normalize(planDetails?.RejectedAction));
      setRetainedActions(normalize(planDetails?.RetainedAction));
    }
  }, [planDetails]);

  // Template options (static list, reserved for future regulation-based changes)
  const [options, setOptions] = useState([]);
  useEffect(() => {
    const updatedOptions = [
      { value: "Review Letter", label: "Review Letter" },
      { value: "COC", label: "COC" },
      { value: "SOF", label: "Statement of Facts" },
      { value: "CKS", label: "CheckList" },
    ];
    setOptions(updatedOptions);
    setTemplateType(updatedOptions[0]?.value || "");
  }, [planVerificationFlags?.data?.Regulations]);

  // Normalize incoming documents payload for table display
  useEffect(() => {
    if (Array.isArray(incomingDocuments)) {
      setAssociatedDocumentsData(flattenDocuments(incomingDocuments));
    } else {
      setAssociatedDocumentsData([]);
    }
  }, [incomingDocuments]);

  // Load associated documents when tab is active and no incoming docs provided
  useEffect(() => {
    if (isActive && (!incomingDocuments || incomingDocuments.length === 0)) {
      GetAssociatedDocuments();
    }
  }, [isActive, planDetails?.PlanId, planDetails?.WcnNumber]);

  // No-op effect to keep verificationEnabled in sync if needed
  useEffect(() => {}, [verificationEnabled]);

  /* --------------------------- Normalizers --------------------------- */

  const getRowKey = (d, i) => {
    const candidate =
      d?.documentMstId ??
      d?.documentId ??
      `${d?.documentFileName || d?.fileName || "doc"}-${d?.revision ?? d?.revisionNumber ?? 0}-${i}`;
    return String(candidate);
  };

  const flattenDocuments = (docs = []) =>
    (Array.isArray(docs) ? docs : []).map((d, i) => {
      const nestedFile = d?.file || d?.fileInfo || d?.attachment || d?.fileAttachment || d?.document || null;

      const fileName = d?.documentFileName ?? d?.fileName ?? nestedFile?.fileName ?? "";
      const revision = d?.revision ?? d?.revisionNumber ?? d?.version ?? "";
      const isAvailableForClient =
        typeof d?.isAvailableForClient === "boolean"
          ? d.isAvailableForClient
          : typeof d?.IsAvailableForClient === "boolean"
          ? d.IsAvailableForClient
          : false;

      return {
        ...d,
        documentFileName: fileName,
        revision,
        isAvailableForClient,
        _rowKey: getRowKey(d, i),
      };
    });

  /* ----------------------------- UI Toggles -------------------------- */

  const onSelectFilesEvent = () => {
    setIsSelectFilesModalOpen(!isSelectFilesModalOpen);
    if (isSelectFilesModalOpen) {
      setSelectedFiles([]);
      setIsApprovedPlanUploadMode(false);
    }
  };

  const onToggleUploadPlanModal = () => {
    setIsUploadPlanModalOpen((prev) => {
      const isClosing = prev === true;
      if (isClosing) {
        setSelectedFiles([]);
        setIsApprovedPlanUploadMode(false);
      }
      return !prev;
    });
  };

  const onVerifierChange = (selected) => setSelectedVerifier(selected?.value);
  const onTemplateOptionChange = (selectedOption) => setTemplateType(selectedOption[0]);

  /* ------------------------------ Table ------------------------------ */

  const VerificationColumns = [
    {
      id: "documentFileName",
      header: "Published Documents",
      accessorKey: "documentFileName",
      Cell: ({ cell }) => <span>{String(cell.getValue() ?? "")}</span>,
    },
    {
      id: "revision",
      header: "Revision Number",
      accessorKey: "revision",
      Cell: ({ cell }) => <span>{String(cell.getValue() ?? "")}</span>,
    },
    {
      id: "isAvailableForClient",
      header: "To be made available to Client?",
      accessorKey: "isAvailableForClient",
      hidden: !(userHasClaim(ClaimType.AVAILABLE_FOR_CLIENT_CHECK) || !userHasFeature?.(FeatureType.AvailableToClient)),
      enableSorting: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Cell: ({ row: { original } }) => {
        const [selected, setSelected] = useState(original.isAvailableForClient === true ? "Yes" : "No");
        const handleChange = async (link) => {
          const prev = selected;
          setSelected(link);
          const ok = await handleAvailableForClient(link, original.documentDtlId);
          if (!ok) setSelected(prev);
        };
        return <Switch links={["Yes", "No"]} selected={selected} onChange={(link) => handleChange(link)} />;
      },
    },
    {
      id: "action",
      header: "Action",
      accessorKey: "action",
      enableSorting: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Cell: ({ row: { original } }) => (
        <Box sx={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
          <Tooltip title="Download">
            <IconButton
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(original.documentDtlId, original.documentFileName);
              }}
            >
              <Download sx={{ color: "#0645AD" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteBtnClick(original, true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  /* ------------------------------ API ------------------------------- */

  // Load associated verification documents
  const GetAssociatedDocuments = async () => {
    try {
      const planId = planDetails?.PlanId ? String(planDetails.PlanId) : "";
      const wcnNumber = planDetails?.WcnNumber || "";

      if (!planId) {
        console.warn("Missing planId for GetDocuments");
        setAssociatedDocumentsData([]);
        return;
      }

      const query = `planId=${encodeURIComponent(planId)}&isVerification=true${
        wcnNumber ? `&wcnNumber=${encodeURIComponent(wcnNumber)}` : ""
      }`;

      const res = await axiosHttp.get(`api/PlanVerification/GetDocuments?${query}`);

      if (res?.data?.isSuccess) {
        const payload = res?.data?.data;
        let documents = [];

        if (Array.isArray(payload)) {
          documents = payload;
        } else if (payload && typeof payload === "object") {
          if (Array.isArray(payload.documents)) {
            documents = payload.documents;
          } else if (Array.isArray(payload.documentReportInfos)) {
            documents = payload.documentReportInfos;
          } else {
            documents = [];
          }
        }

        setAssociatedDocumentsData(flattenDocuments(documents));
      } else {
        setAssociatedDocumentsData([]);
      }
    } catch (error) {
      console.error("GetDocuments failed:", error);
      setAssociatedDocumentsData([]);
    }
  };

  // Verifier options
  const GetVerifyEngineersList = async (input) => {
    try {
      const response = await accessAPIForResponse("GetVerifyEngineers", null, [input]);
      if (response && response.isSuccess) {
        const verifier_options = response.data.map((obj, index) => ({
          label: obj.emailAddress,
          value: obj.emailAddress,
          id: index + 1,
        }));

        if (planDetails && planDetails?.Verifier?.trim()) {
          setSelectedVerifier(planDetails?.Verifier);
        }

        return verifier_options;
      }
      return [];
    } catch (error) {
      console.error("Error fetching verify engineers:", error);
      return [];
    }
  };

  /* --------------------------- Event Handlers ------------------------ */

  // Date picker change
  const handleChangeDate = (date) => {
    SetverificationEnabled(true);
    if (date && date.isValid && date.isValid()) {
      setSelectedVerifyDate(date.format("DD MMM YYYY"));
    } else {
      setSelectedVerifyDate(null);
    }
  };

  // Complete verification
  const onVerificationComplete = () => {
    if (planVerificationFlags?.data?.hasCorrections) {
      emitToasterMsg("error", "Please resolve pending corrections.");
      return;
    }

    const planId = planDetails?.PlanId ? String(planDetails.PlanId) : "";
    const wcnNumber = planDetails?.WcnNumber;

    if (planId && selectedVerifyDate) {
      const parsed = moment(
        selectedVerifyDate,
        [DATE_FORMAT_DD_MMM_YYYY, "DD-MMM-YYYY", "DD MMM YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
        true
      );

      const verificationDate = parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
      if (!verificationDate) {
        emitToasterMsg("error", "Invalid verification date");
        return;
      }

      const query = `planId=${encodeURIComponent(planId)}&verificationDate=${encodeURIComponent(verificationDate)}${
        wcnNumber ? `&wcnNumber=${encodeURIComponent(wcnNumber)}` : ""
      }`;

      axiosHttp
        .get(`api/PlanVerification/VerificationComplete?${query}`)
        .then((response) => {
          if (response?.data?.isSuccess) {
            handleRefresh();
            emitToasterMsg("success", "Verification Completed Successfully");
            SetverificationEnabled(false);
          } else if (response?.data?.errorMessage) {
            emitToasterMsg("error", response.data.errorMessage);
          }
        })
        .catch((error) => {
          console.error(error);
          emitToasterMsg("error", "Verification Complete failed");
        });
    } else {
      emitToasterMsg("error", "Please select verification complete date");
    }
  };

  // Assign verifier
  const onAssignVerifier = () => {
    if (planVerificationFlags?.data?.hasCorrections) {
      emitToasterMsg("error", "Please resolve pending corrections.");
      return;
    }

    const planId = planDetails?.PlanId ? String(planDetails.PlanId) : "";
    const wcnNumber = planDetails?.WcnNumber;

    if (planId && selectedVerifier && selectedVerifier !== "0") {
      const url = `api/PlanVerification/AssignVerifier?planId=${encodeURIComponent(planId)}&verifier=${encodeURIComponent(
        selectedVerifier
      )}${wcnNumber ? `&WcnNumber=${encodeURIComponent(wcnNumber)}` : ""}`;

      axiosHttp.get(url).then((response) => {
        if (response?.data && response?.data?.isSuccess) {
          handleRefresh();
          emitToasterMsg("success", "Assigned Verifier Successfully");
        }
      });
    } else {
      emitToasterMsg("error", "Please select verifier");
    }
  };

  // Update verifier
  const changeVerifierForPlanVerification = () => {
    if (planVerificationFlags?.data?.hasCorrections) {
      emitToasterMsg("error", "Please resolve pending corrections.");
      return;
    }

    const planId = planDetails?.PlanId ? String(planDetails.PlanId) : "";
    const wcnNumber = planDetails?.WcnNumber;

    if (planId && selectedVerifier && selectedVerifier !== "0") {
      const url = `api/PlanVerification/AssignVerifier?planId=${encodeURIComponent(planId)}&verifier=${encodeURIComponent(
        selectedVerifier
      )}${wcnNumber ? `&WcnNumber=${encodeURIComponent(wcnNumber)}` : ""}`;

      axiosHttp.get(url).then((response) => {
        if (response?.data?.isSuccess) {
          handleRefresh();
          emitToasterMsg("success", "Verifier Updated Successfully");
          setShowUpdateVerifierModal(false);
        } else if (response?.data?.errorMessage) {
          emitToasterMsg("error", response.data.errorMessage);
        }
      });
    } else {
      emitToasterMsg("error", "Please select verifier");
    }
  };

  // Retain/Reject action submit
  const onPlanVerificationAction = (formData) => {
    const action = modalAction === "Retain" ? "Retain" : "Reject";
    const remarks = formData?.reason ?? "";
    const planId = planDetails?.PlanId;
    const wcn = planDetails?.WcnNumber;

    if (!planId) {
      emitToasterMsg("error", "Missing plan id");
      return;
    }

    const endpoint = action === "Retain" ? "RetainVerification" : "RejectVerification";
    const url = `api/PlanVerification/${endpoint}?planId=${encodeURIComponent(planId)}&remarks=${encodeURIComponent(remarks)}${
      wcn ? `&wcnNumber=${encodeURIComponent(wcn)}` : ""
    }`;

    axiosHttp
      .get(url)
      .then((response) => {
        if (response && response?.data?.isSuccess) {
          toggleClose();
          handleRefresh();
          emitToasterMsg("success", action === "Retain" ? "PlanVerification retained" : "PlanVerification rejected");
        } else if (response?.data?.errorMessage) {
          emitToasterMsg("error", response?.data?.errorMessage);
        }
      })
      .catch((error) => {
        console.error(error);
        emitToasterMsg("error", "Operation failed");
      });
  };

  // Upload verification documents (multi file), or approved plan (single file)
  const uploadDocuments = (files) => {
    if (!Array.isArray(files) || files.length === 0) {
      emitToasterMsg("error", "Please select at least one file.");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("Files", file));
    formData.append("PlanId", String(planDetails?.PlanId ?? ""));
    formData.append("IsVerification", "true");

    setLoader(true);

    axiosHttp
      .post(
        `api/PlanVerification/UploadDocuments?wcnNumber=${encodeURIComponent(planDetails.WcnNumber ?? "")}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((response) => {
        if (response?.data?.isSuccess === false) {
          const data = response?.data;
          let errorMsg = data?.errorMessage || data?.message || data?.title || "An error occurred while uploading documents.";

          if (data?.errors && typeof data.errors === "object") {
            const parts = Object.entries(data.errors).flatMap(([field, msgs]) => {
              const arr = Array.isArray(msgs) ? msgs : [msgs];
              return arr.map((m) => `${field}: ${m}`);
            });
            if (parts.length > 0) errorMsg = parts.join(" | ");
          }

          emitToasterMsg("error", errorMsg);
          return;
        }

        emitToasterMsg("success", "Files uploaded successfully");
        handleRefresh();
        toggleClose();
      })
      .catch((error) => {
        const data = error?.response?.data;
        let errorMsg = "Failed to upload documents.";

        if (data) {
          if (data?.errors && typeof data.errors === "object") {
            const parts = Object.entries(data.errors).flatMap(([field, msgs]) => {
              const arr = Array.isArray(msgs) ? msgs : [msgs];
              return arr.map((m) => `${field}: ${m}`);
            });
            if (parts.length > 0) {
              errorMsg = parts.join(" | ");
            }
          } else if (data?.errorMessage) {
            errorMsg = data.errorMessage;
          } else if (data?.title) {
            errorMsg = data.status ? `${data.title} (HTTP ${data.status})` : data.title;
          } else if (typeof data === "string") {
            errorMsg = data;
          }
        } else if (error?.message) {
          errorMsg = error.message;
        }

        emitToasterMsg("error", errorMsg);
        console.error(error);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleUploadClick = async () => {
    if (isApprovedPlanUploadMode) {
      if (!selectedFiles?.length) {
        emitToasterMsg("error", "Please select a file.");
        return;
      }
      if (selectedFiles.length > 1) {
        emitToasterMsg("error", "Only one file is allowed for Approved Plan upload.");
        return;
      }
      await uploadApprovedPlan(selectedFiles[0]);
      setIsApprovedPlanUploadMode(false);
      setSelectedFiles([]);
      return;
    }

    uploadDocuments(selectedFiles);
    setSelectedFiles([]);
  };

  // Download template for edit
  const handleDownloadForEdit = () => {
    setLoader(true);

    const requestBody = {
      PlanId: String(planDetails?.PlanId ?? ""),
      TemplateType: templateType ?? "",
      StampingType: "",
    };

    axiosHttp
      .post(`api/PlanVerification/DownloadTemplate?wcnNumber=${encodeURIComponent(planDetails.WcnNumber)}`, requestBody)
      .then((response) => {
        const payload = response?.data;

        if (payload?.isSuccess) {
          const fileData = payload?.data ?? payload ?? {};
          const { fileBytes, fileName, contentType } = fileData;

          if (!fileBytes) {
            setLoader(false);
            emitToasterMsg("error", "No file data received from the server.");
            return;
          }

          try {
            const base64 = fileBytes.includes(",") ? fileBytes.split(",")[1] : fileBytes;

            const byteCharacters = atob(base64);
            const sliceSize = 1024;
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
              const slice = byteCharacters.slice(offset, offset + sliceSize);
              const byteNumbers = new Array(slice.length);
              for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
              }
              byteArrays.push(new Uint8Array(byteNumbers));
            }
            const blob = new Blob(byteArrays, { type: contentType || "application/octet-stream" });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              fileName || (templateType === "Review Letter" ? `${templateType}.docx` : `${templateType}.pdf`)
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setLoader(false);
            emitToasterMsg("success", "Template downloaded successfully.");
          } catch (e) {
            console.error(e);
            setLoader(false);
            emitToasterMsg("error", "Failed to process the downloaded file.");
          }
        } else {
          setLoader(false);
          emitToasterMsg("error", payload?.errorMessage || "PlanVerification status is not valid to generate the template");
        }
      })
      .catch((error) => {
        setLoader(false);
        console.error(error);
        emitToasterMsg("error", "Failed to download the template.");
      });
  };

  // Upload generated template for publishing
  const uploadForPublishing = () => {
    setLoader(true);

    const requestBody = {
      PlanId: String(planDetails?.PlanId ?? ""),
      TemplateType: templateType ?? "",
      StampingType: stampingOnSeemp === "No stamp" ? "" : stampingOnSeemp,
    };

    axiosHttp
      .post(
        `api/PlanVerification/TemplateDocumentToPublish?wcnNumber=${encodeURIComponent(planDetails.WcnNumber)}`,
        requestBody
      )
      .then((response) => {
        const reponseData = response?.data;
        if (reponseData && reponseData?.isSuccess) {
          handleRefresh();
          const successMsg =
            (typeof reponseData?.data === "string" && reponseData.data) ||
            reponseData?.message ||
            "Template uploaded for publishing successfully.";
          emitToasterMsg("success", successMsg);
        } else if (reponseData && !reponseData?.isSuccess) {
          const errMsg = reponseData?.errorMessage || reponseData?.message || "Failed to upload for publishing.";
          emitToasterMsg("error", errMsg);
        }
        setLoader(false);
      })
      .catch((error) => {
        setLoader(false);
        console.error(error);
        emitToasterMsg("error", "Failed to upload for publishing.");
      });
  };

  // Toggle "Available to client"
  const handleAvailableForClient = async (link, documentMstId) => {
    const planId = String(planDetails?.PlanId ?? "");
    const wcnNumber = planDetails?.WcnNumber ?? "";
    const isAvailableToClient = link === "Yes";

    if (!planId || !documentMstId) {
      emitToasterMsg("error", "Missing plan or document identifiers.");
      return false;
    }

    try {
      const url = `api/PlanVerification/SetAvailableToClient${wcnNumber ? `?WcnNumber=${encodeURIComponent(wcnNumber)}` : ""}`;
      const res = await axiosHttp.post(url, {
        DocumentMstId: documentMstId,
        PlanId: planId,
        IsAvailableToClient: isAvailableToClient,
      });

      if (res?.data?.isSuccess) {
        setAssociatedDocumentsData((prev) =>
          Array.isArray(prev)
            ? prev.map((d) => (d.documentMstId === documentMstId ? { ...d, isAvailableToClient: isAvailableToClient } : d))
            : prev
        );
        emitToasterMsg("success", "Availability updated.");
        return true;
      }

      emitToasterMsg("error", res?.data?.errorMessage || "Failed to update availability.");
      return false;
    } catch (error) {
      console.error(error);
      emitToasterMsg("error", "Failed to update availability.");
      return false;
    }
  };

  // Download a verification document
  const handleDownload = (documentMstId, documentName) => {
    const planId = planDetails?.PlanId ? String(planDetails.PlanId) : "";
    const WcnNumber = planDetails?.WcnNumber || "";

    if (!planId || !documentMstId) {
      emitToasterMsg("error", "Missing plan or document identifiers.");
      return;
    }

    setLoader(true);
    axiosHttp
      .get("api/PlanVerification/DownloadDocument", {
        params: {
          planId,
          documentMstId,
          ...(WcnNumber ? { WcnNumber } : {}),
        },
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", documentName || "document");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        emitToasterMsg("success", "Document downloaded successfully.");
      })
      .catch((error) => {
        console.error(error);
        emitToasterMsg("error", "Failed to download document.");
      })
      .finally(() => setLoader(false));
  };

  // Open retain/reject modal
  const handleModal = (action) => {
    if (planDetails.PlanId) {
      setModalAction(action === "Retain" ? "Retain" : "Reject");
      setShowModal(true);
    }
  };

  // Prompt update verifier confirmation
  const handleUpdateVerifierModal = () => {
    if (planDetails.PlanId && selectedVerifier && selectedVerifier !== "0") {
      setShowUpdateVerifierModal(true);
    } else {
      emitToasterMsg("error", "Please select verifier");
    }
  };

  // Close retain/reject modal
  const toggleClose = () => {
    setShowModal(false);
    setModalAction(null);
  };

  // Confirm delete and track selection
  const handleDeleteBtnClick = (row) => {
    setConfirmDelete(true);
    setSelectedDocument(row);
  };

  // Delete document
  const handleDeletingRecord = async () => {
    await axiosHttp({
      url: `api/PlanVerification/DeleteDocument?planId=${encodeURIComponent(planDetails.PlanId)}&documentMstId=${encodeURIComponent(
        selectedDocument.documentDtlId
      )}&wcnNumber=${encodeURIComponent(planDetails.WcnNumber)}`,
      method: "delete",
    })
      .then((res) => {
        setConfirmDelete(false);
        if (res && res?.data?.isSuccess) {
          handleRefresh();
          emitToasterMsg("success", "Deleted Successfully");
        } else if (!res?.data?.isSuccess) {
          emitToasterMsg("error", res?.data?.errorMessage);
        }
      })
      .catch((error) => {
        setConfirmDelete(false);
        console.error(error.response?.data?.errorMessage || error);
      });
  };

  // Refresh parent state and reset transient flags
  const handleRefresh = useCallback(
    async () => {
      const pid = planDetails?.PlanId;
      const wcn = planDetails?.WcnNumber;

      if (!pid || !wcn) {
        console.debug("handleRefresh: missing PlanId/WcnNumber", { pid, wcn });
        return;
      }

      try {
        setLoader(true);
        await (refreshState?.() ?? Promise.resolve());
      } catch (error) {
        console.error("handleRefresh failed:", error);
      } finally {
        SetverificationEnabled(false);
        setLoader(false);
      }
    },
    [planDetails?.PlanId, planDetails?.WcnNumber, refreshState]
  );

  // Open modal for single approved plan upload
  const onUploadPlanDocumentClick = () => {
    if (!planDetails?.PlanId) {
      emitToasterMsg("error", "Missing plan id");
      return;
    }
    setIsApprovedPlanUploadMode(true);
    setSelectedFiles([]);
    if (!isUploadPlanModalOpen) setIsUploadPlanModalOpen(true);
  };

  // Upload the approved plan with stamping
  const uploadApprovedPlan = async (file) => {
    const planId = String(planDetails?.PlanId ?? "");
    const wcn = planDetails?.WcnNumber ?? "";

    const formData = new FormData();
    formData.append("PlanId", planId);
    formData.append("File", file);
    formData.append("IsVerification", "true");
    formData.append("StampType", stampingOnSeemp === "No stamp" ? "" : stampingOnSeemp);

    try {
      setLoader(true);
      const response = await axiosHttp.post(
        `api/PlanVerification/UploadApprovedPlan?wcnNumber=${encodeURIComponent(wcn)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response?.data?.isSuccess) {
        emitToasterMsg("success", "Plan document uploaded successfully.");
        await handleRefresh();
      } else {
        const err = response?.data?.errorMessage || response?.data?.message || "Failed to upload plan document.";
        emitToasterMsg("error", err);
      }
    } catch (error) {
      console.error(error);
      const err = error?.response?.data?.errorMessage || error?.message || "Failed to upload plan document.";
      emitToasterMsg("error", err);
    } finally {
      setLoader(false);
    }
  };
  


  useEffect(() => {
    const fetchInitialWaypointHistory = async () => {
      if (!planDetails?.PlanId) return;

      try {
        setLoader(true);
         const  response = await axiosHttp.get(
            `/api/SeempMonitoring/waypoint-last-history/${encodeURIComponent(planDetails.PlanId)}?seempType=${encodeURIComponent(planType)}`
          );
          
        const data = response?.data;
        if (data) {
          setWaypointSubmissionStatus(data.status);
          setWaypointSubmitDateTime(
            moment.utc(data.submittedOn).isValid()
              ? moment.utc(data.submittedOn).format("DD-MMM-YYYY HH:mm") + " UTC"
              : ""
          );
          setSelectedWaypointType(data.waypointType ||"New");
          setSelectedWaypointAmendType(data.amendmentType ||"Major");
          if (data.status === "Failed") {
            setWaypointStatusMessage(data.statusMessage || "Submission failed");
          } else {
            setWaypointStatusMessage("");
          }
        } else {
          setWaypointSubmissionStatus("");
          setWaypointSubmitDateTime("");
          setWaypointStatusMessage("");
        }
      } catch (error) {
        console.error("Fetch waypoint history failed:", error);
        setWaypointSubmissionStatus("");
        setWaypointSubmitDateTime("");
        setWaypointStatusMessage("");
      } finally {
        setLoader(false);
      }
    };
   fetchInitialWaypointHistory();
    
  }, [isWaypointSubmissionEnabled, isActive, planDetails?.PlanId]);


  // Handle waypoint amendment type change
  const onChangeWaypointAmendType = (selectedOption) => {
		setSelectedWaypointAmendType(selectedOption?.[0]);
  };
  const onChangeWaypointType = (selectedOption) => {
    const type = selectedOption?.[0];
		setSelectedWaypointType(selectedOption?.[0]);
     if (type !== "ClientAmendment") {
    setSelectedWaypointAmendType(amendmentOptions?.[1].value || "Major" );
  }
  };

  // Submit SEEMP plan to WayPoint
  const handleWaypointSubmit = async () => {
   if (!selectedWaypointType) {
  emitToasterMsg("error", `Please select ${planDetails?.Plan || "SEEMP"} Type`);
  return;
}

if (
  selectedWaypointType === "ClientAmendment" &&
  !selectedWaypointAmendType
) {
  emitToasterMsg("error", "Please select Amendment Type");
  return;
}

    if (!planDetails?.PlanId) {
      emitToasterMsg("error", "Missing plan id");
      return;
    }

    setLoader(true);
    try {
       const payload = {
      planId: planDetails.PlanId,
      seempType: SEEMPTYPE[selectedWaypointType],
      amendmentType: AMENDMENTTYPE[selectedWaypointAmendType],
    };  

  var response;
    if(planType === "SEEMP II") {
       response = await axiosHttp.post(
        "/api/SeempMonitoring/SubmitSeempIIToWaypoint",
        payload
      );
    }else if(planType === "SEEMP III") {
       response = await axiosHttp.post(
        "/api/SeempMonitoring/SubmitSeempIIIToWaypoint",
        payload
      );
    }

      // Handle HTTP 200 OK
      if (response.status === 200) {
        const { status, submittedOn, statusMessage } = response?.data;

        const formattedDate = moment.utc(submittedOn).isValid()
          ? moment.utc(submittedOn).format("DD-MMM-YYYY HH:mm") + " UTC"
          : "";
        
        setWaypointSubmitDateTime(formattedDate);
        setWaypointSubmissionStatus(status);

        if (status === "Success") {
          setWaypointStatusMessage("");
          emitToasterMsg("success", "Submitted to WayPoint successfully");
        } else if (status === "Failed") {
          setWaypointStatusMessage(statusMessage || "Submission failed");
          emitToasterMsg("error", "WayPoint submission failed");
        } else {
          setWaypointStatusMessage(statusMessage || "");
          emitToasterMsg("error", `Internal ERP Error: ${statusMessage}`);
        }
      } else {
        // Handle unexpected status codes
        setWaypointSubmissionStatus("ERP Error");
        emitToasterMsg("error", `Internal ERP Error: ${response?.data.statusMessage}`);
      }
    } catch (error) {
      // Handle HTTP 500 Internal Server Error and other errors
      setWaypointSubmissionStatus("");
      setWaypointSubmitDateTime("");
      setWaypointStatusMessage("");
      console.error("WayPoint submission error:", error);
       const errorMessage =
			typeof error.response?.data === "string" ? error.response.data : error.response?.data?.message || error.response?.data?.title || error.message;

	  	emitToasterMsg("error", errorMessage);
    } finally {
      setLoader(false);
     
    }

  };


 
  /* ------------------------------ Render ---------------------------- */

  return (
		<>
			{loader && <ContainedLoader />}

      <MDBRow className="mt-4">
        {/* Left: Verifier and status actions */}
        <MDBCol md={ isWaypointSubmissionEnabled ? "4" : "6" }>
          <div className="verification-container">
            <div
              className={`my-div  ${
                planDetails.VerificationStatus === "Verification Completed" || planDetails.Reviewer === "" ? "disabled" : ""
              }`}
            >
              {/* Rejected details */}
              {planDetails?.VerificationStatus !== "Rejected" &&
                rejectedActions.length > 0 &&
                rejectedActions.map((rejectedAction, idx) => (
                  <div key={`rejected-${idx}`}>
                    <MDBRow className="d-flex justify-content-between mb-1.5">
                      <MDBCol md="3">
                        <Label className="label-text" name="Rejected by :" />
                      </MDBCol>
                      <MDBCol md="9">
                        <Label className="label-text">{rejectedAction?.actionBy}</Label>
                      </MDBCol>
                    </MDBRow>
                    <MDBRow className="d-flex justify-content-between mb-1.5">
                      <MDBCol md="3">
                        <Label className="label-text" name="Remarks :" />
                      </MDBCol>
                      <MDBCol md="9">
                        <Label className="label-text">{rejectedAction?.reason}</Label>
                      </MDBCol>
                    </MDBRow>
                  </div>
                ))}

							{/* Retained details */}
							{planDetails?.VerificationStatus === "Retained" &&
								retainedActions.length > 0 &&
								retainedActions.map((retainedAction, idx) => (
									<div key={`retained-${idx}`}>
										<MDBRow className="d-flex justify-content-between mb-1.5">
											<MDBCol md="3">
												<Label className="label-text" name="Retained by :" />
											</MDBCol>
											<MDBCol md="9">
												<span className="label-text">{retainedAction?.actionBy}</span>
											</MDBCol>
										</MDBRow>
										<MDBRow className="d-flex justify-content-between mb-1.5">
											<MDBCol md="3">
												<Label className="label-text" name="Remarks :" />
											</MDBCol>
											<MDBCol md="9">
												<span className="label-text">{retainedAction?.reason}</span>
											</MDBCol>
										</MDBRow>
									</div>
								))}

              {/* Verifier selection */}
              <MDBRow className="d-flex mb-4">
                <MDBCol md="3">
                  <Label name="Verifier:" bold />
                </MDBCol>
                <MDBCol md="6" className="verifier-dropdown">
                  <div>
                    <AutocompleteDropdown
                      className="inputHeight"
                      id="search-vessel-id"
                      isClearable
                      loadOptions={async (inputText) => {
                        const options = await GetVerifyEngineersList(inputText);
                        return options;
                      }}
                      placeholder={selectedVerifier || "Please Select Verifier"}
                      onChange={onVerifierChange}
                      autoFocus={false}
                      customStyles={{
                        menuList: (provided) => ({
                          ...provided,
                        }),
                      }}
                    />
                  </div>
                </MDBCol>

								{userHasFeature?.(FeatureType.AssignPlanVerificationVerifier) && (
									<div className="d-flex ml-3 mt-1">
										{planDetails?.Verifier?.trim() ? (
											<Button disabled={!showAssignVerifierButton} onClick={() => handleUpdateVerifierModal()}>
												Update Verifier
											</Button>
										) : (
											<Button disabled={!showAssignVerifierButton} onClick={() => onAssignVerifier()}>
												Assign Verifier
											</Button>
										)}
									</div>
								)}
							</MDBRow>

							{/* Verification complete date */}
							<MDBRow className="date-picker-dropdown mt-4">
								<MDBCol md="3" className="d-flex">
									<Label name="Verification Complete Date:" bold />
								</MDBCol>
								<MDBCol>
									<LocalizationProvider
										dateAdapter={AdapterMoment}
										className="verification-datePicker"
										localeText={{
											fieldMonthPlaceholder: (params) => (params.contentType === "digit" ? "MM" : params.format),
										}}
									>
										<DatePicker
											format="DD-MMM-YYYY"
											value={
												selectedVerifyDate
													? moment(selectedVerifyDate, "DD-MMM-YYYY")
													: planDetails?.VerifiedDate
														? moment(planDetails.VerifiedDate, ["DD-MMM-YYYY", "YYYY-MM-DD"])
														: null
											}
											sx={{
												".MuiInputBase-root": {
													height: "35px",
													width: "98%",
												},
												".MuiInputBase-input": {
													textTransform: "uppercase",
												},
											}}
											onChange={handleChangeDate}
											className=""
										/>
									</LocalizationProvider>
								</MDBCol>
							</MDBRow>

              {/* Status actions */}

              <MDBRow className="mt-4 status-btn">
                <MDBCol className="col-8 d-flex justify-content-end ml-5">
                  {userHasFeature?.(FeatureType.VerificationComplete) && (
                    <Button
                      className="verification-complete-btn"
                      disabled={!planVerificationFlags?.data?.showVerificationCompleteButton || !selectedVerifyDate}
                      onClick={onVerificationComplete}
                      primaryBackgroundColor="#DC1E34"
                      primaryColor="#DC1E34"
                    >
                      Verification Complete
                    </Button>
                  )}

									{planVerificationFlags?.data?.showRejectButton && (
										<Button
											className="reject-btn ml-4"
											primaryBackgroundColor={COLORS.RED.DARK}
											primaryColor={COLORS.RED.DARK}
											onClick={() => handleModal("Reject")}
										>
											Reject
										</Button>
									)}

									{planVerificationFlags?.data?.showRetainButton && userHasFeature?.(FeatureType.RetainPlanVerification) && (
										<Button
											className="reject-btn ml-4"
											primaryBackgroundColor={COLORS.RED.DARK}
											primaryColor={COLORS.RED.DARK}
											onClick={() => handleModal("Retain")}
										>
											Retain
										</Button>
									)}
								</MDBCol>
							</MDBRow>
						</div>
					</div>
				</MDBCol>

        {/* Right: Template generator and uploads */}
        <MDBCol md={ isWaypointSubmissionEnabled ? "5" : "6" } className="verification-template-container mt-4">
          <div className="verification-template">
            <MDBRow className="d-flex mb-4">
              <MDBCol md="2">
                <Label name="Template Generator:" className="mt-2" bold />
              </MDBCol>
              <MDBCol md="4" className= "review-dropdown" >
                <GenericDropdown options={options} selected={templateType} handleChange={onTemplateOptionChange} className= "review-dropdown"/>
              </MDBCol>
              <div className="d-flex ml-3 download-btn-container">
                <div>
                  <Button disabled={false} onClick={handleDownloadForEdit}>
                    Download for edit
                  </Button>
                </div>
                <div className="ml-3">
                  <Button disabled={false} onClick={uploadForPublishing} variant="secondary">
                    Upload for publishing
                  </Button>
                </div>
              </div>       
               <MDBCol md=""></MDBCol>
                <MDBCol></MDBCol>                     
            </MDBRow>

            <MDBRow className="d-flex justify-content-between document-type">
              <MDBCol md="2">
                <Label name="New Document Type:" className="mt-2" bold />
              </MDBCol>
              <MDBCol>
                <Button disabled={false} variant="secondary" className="ml-1" onClick={onSelectFilesEvent}>
                  Select Files
                </Button>
              </MDBCol>
            </MDBRow>

            <MDBRow className="d-flex justify-content-between document-type mt-3">
              <MDBCol md="2">
                <Label name="Stamping on SEEMP:" className="mt-2" bold />
              </MDBCol>
              <MDBCol>
                <div className="regulation-radio-container d-flex align-items-center pb-2">
                  <RadioInput
                    label="No stamp"
                    gap="true"
                    checked={stampingOnSeemp === "No stamp"}
                    onChange={() => setStampingOnSeemp("No stamp")}
                  />
                  <RadioInput label="S40" gap="true" checked={stampingOnSeemp === "S40"} onChange={() => setStampingOnSeemp("S40")} />
                  <RadioInput label="G1" gap="true" checked={stampingOnSeemp === "G1"} onChange={() => setStampingOnSeemp("G1")} />
                </div>
              </MDBCol>
              <MDBCol md="3" className="pl-0">
                <Button disabled={false} variant="secondary" className="ml-2 upload-plan-btn" onClick={onUploadPlanDocumentClick}>
                  Upload Plan Document
                </Button>
              </MDBCol>
            </MDBRow>
          </div>
        </MDBCol>
        {/*SEEMP waypoint submission */}
        {isWaypointSubmissionEnabled && (
          <MDBCol md="3">
                      <div style={{ borderLeft: "1px solid #8a8a8a", paddingLeft: "24px", paddingBottom: "20px" }}>        
                        <h6>
                          <strong>WayPoint Submission</strong>
                        </h6>
          
                        {/* dropdown buttons inline */}

                        <div className="mt-3 d-flex ">
                          <Label name={` ${planDetails?.Plan || "SEEMP"} Type:`} bold className="mt-1 mr-4 seemp-type-margin" htmlFor="type-dropdown" />   
                                                                        
                          <div id="type-dropdown" role="combobox" aria-controls="type-menu" aria-expanded={!!selectedWaypointType} >
                       <GenericDropdown options={typeOptions} placeholder={`Select ${planDetails?.Plan || "SEEMP"} Type`} selected={selectedWaypointType} handleChange={onChangeWaypointType} />
                          </div>

                        </div>
                       {selectedWaypointType === "ClientAmendment" && (
                        <div className="mt-3 d-flex ">
                          <Label name="Amendment Type:" bold className="mt-1 mr-4" htmlFor="amendment-type-dropdown" />   

                          <div id="amendment-type-dropdown" role="combobox" aria-controls="amendment-type-menu" aria-expanded={!!selectedWaypointAmendType}>
                       <GenericDropdown options={amendmentOptions} placeholder="Select Amendment Type" selected={selectedWaypointAmendType} handleChange={onChangeWaypointAmendType} />
                          </div>

                        </div>
                        )}
          
                        <div className="mt-4 d-flex">
                          <Button
                            disabled={!["Approved", "Under Verification"].includes(planDetails.VerificationStatus)}
                            primaryBackgroundColor="#DC1E34"
                            primaryColor="#DC1E34"
                            onClick={handleWaypointSubmit}
                          >
                            Submit to WayPoint
                          </Button>                       
                        </div>
                      
          
                        <div className="mt-3">
                          <div className="d-flex">
                            <Label name="Submission Status:" bold />
                            <small
                              className="ml-2"
                              style={{
                                color: waypointSubmissionStatus === "Failed" || waypointSubmissionStatus === "ERP Error" ? "red" : "inherit",
                              }}
                            >
                              {waypointSubmissionStatus}
                            </small>
                          </div>
          
                          <div className="d-flex">
                            <Label name="Submitted Date & Time:" className="mt-2" bold />
                            <small className="ml-2 mt-2">{waypointSubmitDateTime}</small>
                          </div>
                         
                          {waypointSubmissionStatus === "Failed" && waypointStatusMessage && (
                            <div className="d-flex">
                              <Label name="Reason for Failure:" className="mt-2" bold />
          
                              {waypointStatusMessage.length > 50 ? (
                                <small
                                  className="ml-2 mt-2"
                                  style={{ color: "red" }}
                                  
                                >
                                  {waypointStatusMessage.slice(0, 50)}...{" "}
                                  <span style={{ textDecoration: "underline", color: "#02162b",cursor:"pointer" }}
                                    onClick={() => {
                                      setwayPointErrorMessage(waypointStatusMessage);
                                      setIsWayPointMessageModalOpen(true);
                                    }}
                                  >
                                    (Click to view full details)
                                  </span>
                                </small>
                              ) : (
                                <small className="ml-2 mt-2" style={{ color: "red" }}>
                                  {waypointStatusMessage}
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </div> 	
                    </MDBCol>
        )}
        {iswayPointMessageModalOpen && (
                  <AbsModal
                    isOpen={iswayPointMessageModalOpen}
                    title="Failure Reason"
                    onClose={setIsWayPointMessageModalOpen}
                    close={() => setIsWayPointMessageModalOpen(false)}
                  >
                    <div style={{ padding: "10px", color: "red", wordBreak: "break-word" }}>
                      {wayPointErrorMessage}
                    </div>
                  </AbsModal>
                )}
      </MDBRow>

      {/* Published documents table + modals */}
      <div>
        <div className="mt-4">
          <div className="custom-table-margin">
            <CustomTable
              columnsData={VerificationColumns.filter((column) => !column.hidden)}
              renderTableData={associatedDocumentsData}
              customProps={{
                enableSort: false,
                enableToptoolbar: false,
                handleExportTable: false,
                showExportButton: false,
                showSwitch: true,
                rowKeyAccessor: "_rowKey",
                maxHeight: "400px",
              }}
            />
          </div>

					{isSelectFilesModalOpen && (
						<AbsModal isOpen={isSelectFilesModalOpen} title="Select Files" onClose={setIsSelectFilesModalOpen} close={onSelectFilesEvent}>
							<VerificationSelectFiles
								handleUploadClick={handleUploadClick}
								toggleModal={onSelectFilesEvent}
								toggleClose={onSelectFilesEvent}
								selectedFiles={selectedFiles}
								setSelectedFiles={setSelectedFiles}
							/>
						</AbsModal>
					)}

					{isUploadPlanModalOpen && (
						<AbsModal isOpen={isUploadPlanModalOpen} title="Upload Plan" onClose={setIsUploadPlanModalOpen} close={onToggleUploadPlanModal}>
							<PlanUploadModal
								handleUploadClick={handleUploadClick}
								toggleModal={onToggleUploadPlanModal}
								toggleClose={onToggleUploadPlanModal}
								selectedFiles={selectedFiles}
								setSelectedFiles={setSelectedFiles}
							/>
						</AbsModal>
					)}

					{showModal && (
						<AbsModal isOpen={showModal} title={modalAction === "Retain" ? "Retain" : "Reject"} onClose={setShowModal} close={toggleClose}>
							<div className="pb-5">
								<form onSubmit={handleSubmit(onPlanVerificationAction)}>
									<div className="confirmation-modal-container p-3">
										<Label
											name={`Please enter the reason plan has been placed in a  ${
												modalAction === "Retain" ? "retaining" : "rejection"
											} status`}
											required
											bold
										/>
										<Controller
											name="reason"
											control={control}
											rules={{ required: "Reason is required" }}
											render={({ field }) => (
												<>
													<Input
														id="reason"
														type="text"
														onChange={field.onChange}
														value={field.value}
														autoFocus={false}
														className="name-input mt-2"
														placeholder="Enter your reason"
													/>
													{errors["reason"] && <p className="error">{errors["reason"].message}</p>}
												</>
											)}
										/>
										<div className="d-flex justify-content-end mt-5 mr-3">
											<button type="submit" className="mr-3 save-button" id="save-update-btn-portcode">
												{modalAction === "Retain" ? "Retain" : "Reject"}
											</button>
											<button
												id="cancel-btn-portcode"
												type="button"
												className="clear-button"
												onClick={() => {
													toggleClose();
													reset();
												}}
											>
												Cancel
											</button>
										</div>
									</div>
								</form>
							</div>
						</AbsModal>
					)}

					{showUpdateVerifierModal && (
						<div className="confirmation-modal-container">
							<Modal
								isOpen={showUpdateVerifierModal}
								modalHeader
								padding={"70px"}
								headerHeight={"50px"}
								showFooter
								onCloseClickListener={() => {
									setShowUpdateVerifierModal(false);
								}}
								footer={
									<div className="d-flex justify-content-end fixed-action-btns p-4">
										<Button
											className="modal-delete-btn"
											onClick={() => changeVerifierForPlanVerification()}
											primaryColor={COLORS.RED.DARK}
											primaryBackgroundColor={COLORS.RED.LIGHT}
										>
											Confirm
										</Button>
										<Button
											className="modal-cancel-btn"
											onClick={() => {
												setShowUpdateVerifierModal(false);
											}}
										>
											Cancel
										</Button>
									</div>
								}
							>
								<div>Access will be removed from the current verifier and granted to the selected verifier.</div>
							</Modal>
						</div>
					)}

					<div className="confirmation-modal-container">
						<Modal
							isOpen={confirmDelete}
							modalHeader
							padding={"70px"}
							headerHeight={"50px"}
							showFooter
							onCloseClickListener={() => {
								setConfirmDelete(false);
							}}
							footer={
								<div className="d-flex justify-content-end fixed-action-btns p-4">
									<Button
										className="modal-delete-btn"
										onClick={handleDeletingRecord}
										primaryColor={COLORS.RED.DARK}
										primaryBackgroundColor={COLORS.RED.LIGHT}
									>
										Delete
									</Button>
									<Button
										className="modal-cancel-btn"
										onClick={() => {
											setConfirmDelete(false);
										}}
									>
										Cancel
									</Button>
								</div>
							}
						>
							<div>Are you sure you want to delete this document?</div>
						</Modal>
					</div>
				</div>
			</div>
		</>
  );
};

export default Verification;
